/**
 * Who has to opt in before analytics stores anything in their browser, and
 * a storage wrapper that respects it.
 *
 * Region: middleware.ts sets a `tf_cc` cookie with the visitor's country from
 * Vercel's IP lookup (a 2-letter code, no identifier). If that cookie is
 * missing, the browser's time zone is the fallback.
 *
 * Consent countries (EU, EEA, UK, Switzerland) get no analytics storage until
 * the visitor clicks Accept: the tracker still counts page views and actions,
 * but keeps its visit ID in memory, so one visit is linked and nothing is
 * saved on the device (October 10, 2026). Everyone else is unchanged.
 *
 * Washington (October 12, 2026): its My Health My Data Act treats a device ID
 * linked to the health pages someone reads as consumer health data, and
 * analytics is not needed to provide anything the visitor asked for, so
 * Washington visitors get the same opt-in as Europe. middleware.ts sets a
 * `tf_rg` cookie ("WA") from Vercel's IP lookup for those visitors only.
 */

export const CONSENT_COUNTRIES = new Set([
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU", "IE", "IT", "LV", "LT", "LU",
  "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES", "SE", // EU
  "IS", "LI", "NO", // EEA
  "GB", "CH", // UK, Switzerland
]);

const CONSENT_TIMEZONES = new Set([
  "Europe/Amsterdam", "Europe/Athens", "Europe/Berlin", "Europe/Bratislava", "Europe/Brussels", "Europe/Bucharest",
  "Europe/Budapest", "Europe/Copenhagen", "Europe/Dublin", "Europe/Helsinki", "Europe/Lisbon", "Europe/Ljubljana",
  "Europe/Luxembourg", "Europe/Madrid", "Europe/Malta", "Europe/Nicosia", "Europe/Paris", "Europe/Prague",
  "Europe/Riga", "Europe/Rome", "Europe/Sofia", "Europe/Stockholm", "Europe/Tallinn", "Europe/Vienna",
  "Europe/Vilnius", "Europe/Warsaw", "Europe/Zagreb", "Europe/Oslo", "Atlantic/Reykjavik", "Europe/Vaduz",
  "Europe/London", "Europe/Zurich", "Europe/Busingen", "Europe/Gibraltar", "Europe/Guernsey", "Europe/Isle_of_Man",
  "Europe/Jersey", "Europe/Mariehamn", "Atlantic/Canary", "Atlantic/Madeira", "Atlantic/Azores",
]);

/** US states whose visitors opt in first. Keep in sync with middleware.ts. */
export const CONSENT_US_STATES = new Set(["WA"]);

export const CONSENT_KEY = "tf_cookie_consent";

function regionCookie(): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(/(?:^|;\s*)tf_rg=([A-Z]{2})/);
  return m ? m[1] : null;
}

function countryCookie(): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(/(?:^|;\s*)tf_cc=([A-Z]{2})/);
  return m ? m[1] : null;
}

/** True when this visitor must opt in before analytics stores anything. */
export function consentRequired(): boolean {
  const cc = countryCookie();
  if (cc === "US") { const rg = regionCookie(); return !!rg && CONSENT_US_STATES.has(rg); }
  if (cc) return CONSENT_COUNTRIES.has(cc);
  try {
    return CONSENT_TIMEZONES.has(Intl.DateTimeFormat().resolvedOptions().timeZone);
  } catch {
    return true; // can't tell: be safe
  }
}

export function storedChoice(): "accepted" | "declined" | null {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    return v === "accepted" || v === "declined" ? v : null;
  } catch {
    return null;
  }
}

/** Browser signal asking sites not to sell or share data (Global Privacy Control). */
export function gpcOn(): boolean {
  try { return (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true; } catch { return false; }
}

/** May analytics keep IDs in local or session storage? */
export function analyticsStorageAllowed(): boolean {
  // Anyone, anywhere, can turn saving off on /consumer-health-data.
  if (storedChoice() === "declined") return false;
  if (!consentRequired()) return true;
  return storedChoice() === "accepted";
}

// In-memory stand-in for local and session storage, for visitors who have
// not opted in. Lives as long as the page; client-side navigation keeps it.
const memory = new Map<string, string>();

export const analyticsStorage = {
  get(kind: "local" | "session", key: string): string | null {
    if (!analyticsStorageAllowed()) return memory.get(`${kind}:${key}`) ?? null;
    try { return (kind === "local" ? localStorage : sessionStorage).getItem(key); } catch { return memory.get(`${kind}:${key}`) ?? null; }
  },
  set(kind: "local" | "session", key: string, value: string): void {
    if (!analyticsStorageAllowed()) { memory.set(`${kind}:${key}`, value); return; }
    try { (kind === "local" ? localStorage : sessionStorage).setItem(key, value); } catch { memory.set(`${kind}:${key}`, value); }
  },
  /** Remove analytics values saved before consent was required (for
   *  example a Washington visitor from before October 12, 2026) or after a
   *  Decline. Leaves the consent choice itself in place. */
  clearSaved(): void {
    try { for (const k of ["tf_device_id", "tf_first_visit"]) localStorage.removeItem(k); } catch { /* storage blocked */ }
    try { for (const k of ["tf_session_start", "tf_referrer", "tf_traffic_source", "tf_traffic_medium", "tf_pages_viewed"]) sessionStorage.removeItem(k); } catch { /* storage blocked */ }
  },
  /** After Accept: move this visit's in-memory values into real storage, so
   *  the visit keeps one ID instead of starting a second one. */
  persist(): void {
    for (const [k, v] of memory) {
      const [kind, ...rest] = k.split(":");
      try { (kind === "local" ? localStorage : sessionStorage).setItem(rest.join(":"), v); } catch { /* storage blocked */ }
    }
    memory.clear();
  },
};
